import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ChevronDownIcon, ChevronUpIcon, List, RawButton, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import { DEFAULT_EMPTY_CHARACTER } from '@/common/constants';
import { useSnackbar } from '@/modules/core';
import { SnackbarState } from '@/modules/core/core.types';
import { useCronBuilder } from '@/utils/handlers';

import { ReportTab } from '../../report.types';
import {
  REPORT_TYPE_TITLES,
  ScheduledReportItemPayload,
  ScheduledReportStatus,
} from '../ScheduledReport.types';

import { useDeactivateScheduledReportQuery } from './queries';
import ScheduledReportItemDeactivationModal from './ScheduledReportItemDeactivationModal';
import ScheduledReportItemEditModal from './ScheduledReportItemEditModal';
import ScheduledReportItemFilters from './ScheduledReportItemFilters';

const StyledText = styled(Text)`
  &:hover {
    color: ${({ theme }) => theme.colors.textAccent};
  }
`;
interface Props extends ScheduledReportItemPayload {}
const ScheduledReportItem: React.FC<Props> = ({
  id,
  name,
  cron,
  reportType,
  status,
  reportFilter,
  nextScheduledDate,
  startDate,
  reportGroupType,
  ...rest
}) => {
  const [isDeactivation, setDeactivation] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const { parseCronString, frequency } = useCronBuilder({
    ignoreTimezones: true,
    // timezone: Timezone.EDT,
  });

  const { isLoading: isLoadingDeactivation, handleSubmit: handleSubmitDeactivation } =
    useDeactivateScheduledReportQuery({ id });

  const [isExpand, setExpand] = useState(false);
  const handleToggle = useCallback(() => {
    setExpand(!isExpand);
  }, [isExpand]);
  const currentReportType = useMemo(() => {
    return REPORT_TYPE_TITLES[reportType as string];
  }, [reportType]);
  useEffect(() => {
    if (cron) {
      parseCronString(cron);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cron]);
  const currentStatusNode = useMemo(() => {
    if (status === ScheduledReportStatus.Deactivated) {
      return (
        <Box align="center" direction="row">
          <Box background="danger" width="10px" height="10px" round="50%"></Box>
          <Text size="medium" weight={400} margin={{ left: 'spacing8' }}>
            Deactivated
          </Text>
        </Box>
      );
    }
    return (
      <Box align="center" direction="row">
        <Box background="success" width="10px" height="10px" round="50%"></Box>
        <Text size="medium" weight={400} margin={{ left: 'spacing8' }}>
          Active
        </Text>
      </Box>
    );
  }, [status]);

  const handleEdit = useCallback(() => {
    setIsEditModal(true);
  }, []);
  const { handleAddSnackbar } = useSnackbar();
  const handleDeactivate = useCallback(async () => {
    await handleSubmitDeactivation();
    setDeactivation(false);
    handleAddSnackbar({
      text: `${name} was deactivated successfully`,
      state: SnackbarState.positive,
      closeIcon: true,
    });
  }, [handleAddSnackbar, handleSubmitDeactivation, name]);

  return (
    <List.Item
      testId={`scheduled-report_item_${id}`}
      direction="column"
      justify="center"
      // onClick={handleToggle}
    >
      <ScheduledReportItemEditModal
        visible={isEditModal}
        onSetVisible={setIsEditModal}
        id={id}
        name={name}
        cron={cron}
        reportType={reportType}
        status={status}
        reportFilter={reportFilter}
        nextScheduledDate={nextScheduledDate}
        startDate={startDate}
        reportGroupType={reportGroupType}
        {...rest}
      />
      <ScheduledReportItemDeactivationModal
        visible={isDeactivation}
        isLoading={isLoadingDeactivation}
        onSetVisible={setDeactivation}
        onDeactivation={handleDeactivate}
      />
      <Box height={{ min: '40px' }} align="center" direction="row" fill>
        <Box direction="row" align="center" style={{ flex: 4 }}>
          <RawButton
            fill
            data-testId={`scheduled-report_item_button_${id}`}
            onClick={handleToggle}
            style={{ display: 'block' }}
          >
            <Box direction="row" align="center">
              {isExpand ? (
                <ChevronUpIcon size="24px" color="iconPrimary" />
              ) : (
                <ChevronDownIcon size="24px" color="iconPrimary" />
              )}
              <StyledText
                data-testid={`scheduled-report_item_${id}_name`}
                truncate
                title={name}
                weight={700}
                margin={{ left: 'spacing12' }}
              >
                {name}
              </StyledText>
            </Box>
          </RawButton>
        </Box>
        <Box width="12px" />
        <List.Item.Text
          testId={`scheduled-report_item_${id}_report-type`}
          truncate
          title={currentReportType}
          weight={400}
          flex={3}
        >
          {currentReportType || DEFAULT_EMPTY_CHARACTER}
        </List.Item.Text>
        <List.Item.Text testId={`scheduled-report_item_${id}_frequency`} weight={400} flex={2}>
          {frequency || DEFAULT_EMPTY_CHARACTER}
        </List.Item.Text>
        <List.Item.Text
          testId={`scheduled-report_item_${id}_start-date`}
          truncate
          title={currentReportType}
          weight={400}
          flex={2}
        >
          {startDate || DEFAULT_EMPTY_CHARACTER}
        </List.Item.Text>
        <List.Item.Text
          testId={`scheduled-report_item_${id}_next-scheduled-date`}
          truncate
          title={currentReportType}
          weight={400}
          flex={3}
        >
          {nextScheduledDate || DEFAULT_EMPTY_CHARACTER}
        </List.Item.Text>
        <Box style={{ flex: 2 }}>{currentStatusNode}</Box>

        <List.Item.Options
          isDisabled={status === ScheduledReportStatus.Deactivated}
          testId="scheduled-report_item_options_button"
          title={
            status === ScheduledReportStatus.Deactivated ? 'Deactivated Schedules can’t be edited' : undefined
          }
        >
          <List.Item.Options.Item testId="scheduled-report_item_options_details_button" onClick={handleEdit}>
            Edit
          </List.Item.Options.Item>
          {status !== ScheduledReportStatus.Deactivated && (
            <List.Item.Options.Item
              color="danger"
              testId="scheduled-report_item_options_edit_button"
              onClick={() => setDeactivation(true)}
            >
              Deactivate
            </List.Item.Options.Item>
          )}
        </List.Item.Options>
      </Box>
      {isExpand && (
        <>
          <Box height="spacing16" />
          <ScheduledReportItemFilters isWithSSN={reportGroupType === ReportTab.Report} {...reportFilter} />
        </>
      )}
    </List.Item>
  );
};
export default ScheduledReportItem;
